import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { LoginScreen } from '@/screens/LoginScreen';
import { RegistrationScreen } from '@/screens/RegistrationScreen';
import { ResetPasswordScreen } from '@/screens/ResetPasswordScreen';
import { CompanySelectScreen } from '@/screens/CompanySelectScreen';
import { AppLayout } from '@/screens/AppLayout';
import { HomeScreen } from '@/screens/HomeScreen';
import { ActivityReportsScreen } from '@/screens/ActivityReportsScreen';
import { CaseReportsScreen } from '@/screens/CaseReportsScreen';
import { CallsForServiceScreen } from '@/screens/CallsForServiceScreen';
import { AnalyticsReportsScreen } from '@/screens/AnalyticsReportsScreen';
import { LegacyUrlRedirect } from '@/router/LegacyUrlRedirect';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { authLoading, isAuthenticated } = useAuth();
  if (authLoading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function HtmlCodeRedirect({ basePath }: { basePath: string }) {
  const [params] = useSearchParams();
  const code = params.get('code');
  if (code) return <Navigate to={`/${basePath}/${code}`} replace />;
  return <Navigate to="/login" replace />;
}

function AuthRedirect() {
  const { authLoading, isAuthenticated } = useAuth();
  if (authLoading) return null;
  return <Navigate to={isAuthenticated ? '/home' : '/login'} replace />;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Legacy URL handler for /?home, /?activity-reports, etc. */}
        <Route path="/index.html" element={<LegacyUrlRedirect />} />
        <Route path="/reset-password.html" element={<HtmlCodeRedirect basePath="reset-password" />} />
        <Route path="/registration.html" element={<HtmlCodeRedirect basePath="registration" />} />

        {/* Public routes */}
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/registration/:code" element={<RegistrationScreen />} />
        <Route path="/reset-password/:code" element={<ResetPasswordScreen />} />

        {/* Company selection (authenticated but no company selected yet) */}
        <Route path="/company-select" element={<CompanySelectScreen />} />

        {/* Protected routes with navbar */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/home" element={<HomeScreen />} />
          <Route path="/activity-reports" element={<ActivityReportsScreen />} />
          <Route path="/case-reports" element={<CaseReportsScreen />} />
          <Route path="/calls-for-service" element={<CallsForServiceScreen />} />
          <Route path="/analytics-reports" element={<AnalyticsReportsScreen />} />
        </Route>

        {/* Root and catch-all */}
        <Route path="/" element={<LegacyUrlRedirect />} />
        <Route path="*" element={<AuthRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
